import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "bootstrap-icons/font/bootstrap-icons.css";

import { finalizeSessionPayment } from "../../apis/payments";
import { APP_ROUTES } from "../../constants/appRoutes";
import { fetchCurrentPlan } from "../../store/planSlice";
import {
    formatPaymentAmount,
    formatPaymentDate,
    unwrapPaymentResponse,
} from "../../utils/normalizePayment";
import FullWidthLayout from "../shared/FullWidthLayout";

function DetailRow({ label, value, mono = false }) {
    return (
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
            <span className="shrink-0 text-sm text-slate-500">{label}</span>
            <span
                className={`text-right text-sm font-semibold text-slate-900 ${mono ? "font-mono text-xs tracking-wide" : ""}`}
            >
                {value}
            </span>
        </div>
    );
}

function PaymentStateCard({ children }) {
    return (
        <FullWidthLayout>
            <div className="mx-auto w-full max-w-lg py-8 md:py-14">
                <article className="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.08)] sm:p-6 md:p-8">
                    {children}
                </article>
            </div>
        </FullWidthLayout>
    );
}

export default function PaymentSuccess() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");

    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(Boolean(sessionId));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user || !sessionId) return undefined;

        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await finalizeSessionPayment(sessionId);
                if (cancelled) return;

                setPayment(unwrapPaymentResponse(data));
                await dispatch(fetchCurrentPlan()).unwrap();
            } catch (err) {
                if (cancelled) return;
                const message =
                    typeof err === "string"
                        ? err
                        : err?.response?.data?.message ||
                          err?.message ||
                          "Could not confirm your payment. Please try again.";
                setError(message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user, sessionId, dispatch]);

    if (!user) {
        return <Navigate to={APP_ROUTES.HOME} replace />;
    }

    if (!sessionId) {
        return (
            <PaymentStateCard>
                <div className="text-center">
                    <p className="mb-2 text-lg font-bold text-slate-900">Missing payment session</p>
                    <p className="mb-6 text-sm text-slate-600">
                        We could not find a checkout session to confirm. If you completed a payment,
                        check your email for a receipt.
                    </p>
                    <Link to={APP_ROUTES.PLANS} className="btn btn-dark-app no-underline">
                        View plans
                    </Link>
                </div>
            </PaymentStateCard>
        );
    }

    if (loading) {
        return (
            <PaymentStateCard>
                <div className="flex flex-col items-center py-6 text-center">
                    <span
                        className="spinner-border spinner-border-sm text-slate-700"
                        role="status"
                        aria-hidden
                    />
                    <p className="mt-4 text-sm font-medium text-slate-700">Confirming your payment…</p>
                </div>
            </PaymentStateCard>
        );
    }

    if (error || !payment) {
        return (
            <PaymentStateCard>
                <div className="text-center">
                    <p className="mb-2 text-lg font-bold text-slate-900">Payment confirmation failed</p>
                    <p className="mb-6 text-sm text-slate-600">{error || "Something went wrong."}</p>
                    <Link to={APP_ROUTES.PLANS} className="btn btn-dark-app no-underline">
                        Back to plans
                    </Link>
                </div>
            </PaymentStateCard>
        );
    }

    const amountPaid = formatPaymentAmount(payment);
    const paidAt = formatPaymentDate(payment.paid_at ?? payment.created_at);

    return (
        <FullWidthLayout>
            <div className="mx-auto w-full max-w-lg py-8 md:py-14">
                <article className="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.08)] sm:p-6 md:p-8">
                        <div className="mb-6 flex flex-col items-center text-center">
                            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-emerald-900 bg-emerald-50 shadow-sm">
                                <i
                                    className="bi bi-check-lg text-6xl text-emerald-600"
                                    aria-hidden
                                />
                            </div>
                            <p className="my-4 text-lg font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Payment successful
                            </p>
                            <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                                You&apos;re all set
                            </h1>
                            <p className="mb-0 max-w-sm text-sm leading-relaxed text-slate-600">
                                Your subscription to{" "}
                                <span className="font-semibold text-slate-800">{payment.plan_name}</span>{" "}
                                is active.
                            </p>
                            <p className="mb-0 max-w-sm text-xs leading-relaxed text-slate-600">
                                A receipt has been sent to your email.
                            </p>
                        </div>

                        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-1">
                            <DetailRow label="Amount paid" value={amountPaid} />
                            <DetailRow label="Plan" value={payment.plan_name} />
                            <DetailRow label="Payment ID" value={payment.payment_id} mono />
                            <DetailRow label="Paid on" value={paidAt} />
                            {sessionId ? (
                                <DetailRow
                                    label="Session ID"
                                    value={
                                        sessionId.length > 24
                                            ? `${sessionId.slice(0, 12)}…${sessionId.slice(-8)}`
                                            : sessionId
                                    }
                                    mono
                                />
                            ) : null}
                        </div>

                        {payment.receipt_url || payment.invoice_url ? (
                            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {payment.receipt_url ? (
                                    <a
                                        href={payment.receipt_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-dark-outline-app flex w-full items-center justify-center gap-2 no-underline"
                                    >
                                        <i className="bi bi-receipt-cutoff" aria-hidden />
                                        Download receipt
                                    </a>
                                ) : null}
                                {payment.invoice_url ? (
                                    <a
                                        href={payment.invoice_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-dark-outline-app flex w-full items-center justify-center gap-2 no-underline"
                                    >
                                        <i className="bi bi-file-earmark-text" aria-hidden />
                                        Download invoice
                                    </a>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Link
                                to={APP_ROUTES.HOME}
                                className="btn btn-dark-outline-app flex w-full items-center justify-center gap-2 no-underline"
                            >
                                <i className="bi bi-house-door" aria-hidden />
                                Home
                            </Link>
                            <Link
                                to={APP_ROUTES.CAMPAIGN_WIZARD}
                                className="btn btn-dark-app flex w-full items-center justify-center gap-2 no-underline"
                            >
                                <i className="bi bi-megaphone" aria-hidden />
                                Create campaign
                            </Link>
                        </div>
                    </article>
            </div>
        </FullWidthLayout>
    );
}
